import { useState, useCallback, useMemo, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { track, AnalyticsEvents } from '@/lib/services/analytics'
import { Button } from '@/components/ui/button'
import { ToggleChip } from '@/components/ui/toggle-chip'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { usersApi } from '@/lib/api'
import { useTheme } from '@/lib/theme-provider'
import { OnboardingStepProps } from '@/types/components/onboarding'
import { FadeUp } from '../FadeUp'
import { REGIONS, countryName } from './market-data'

export const MarketsStep = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function MarketsStep({ wizardState, onUpdateState, onNext, onSkip, className }, ref) {
    const { t, i18n } = useTranslation(['onboarding', 'common'])
    const [isSaving, setIsSaving] = useState(false)
    const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set())
    const { resolvedTheme } = useTheme()
    const tone = resolvedTheme === 'dark' ? 'dark' : 'light'
    const shouldReduceMotion = useReducedMotion()
    const duration = shouldReduceMotion ? 0 : 0.2

    const selectedCountries = wizardState.selectedCountries

    const toggleRegion = useCallback(
      (regionId: string) => {
        setExpandedRegions((prev) => {
          const copy = new Set(prev)
          if (copy.has(regionId)) {
            copy.delete(regionId)
          } else {
            copy.add(regionId)
          }
          return copy
        })
      },
      [],
    )

    const toggleCountry = useCallback(
      (code: string) => {
        const next = selectedCountries.includes(code)
          ? selectedCountries.filter((c) => c !== code)
          : [...selectedCountries, code]
        onUpdateState({ selectedCountries: next })
      },
      [selectedCountries, onUpdateState],
    )

    const toggleAllInRegion = useCallback(
      (regionCountries: Array<string>) => {
        const allSelected = regionCountries.every((c) => selectedCountries.includes(c))
        const next = allSelected
          ? selectedCountries.filter((c) => !regionCountries.includes(c))
          : Array.from(new Set([...selectedCountries, ...regionCountries]))
        onUpdateState({ selectedCountries: next })
      },
      [selectedCountries, onUpdateState],
    )

    const uiLocale = i18n.resolvedLanguage ?? i18n.language

    const regionLabel = useCallback(
      (regionId: string) => t(`markets.regions.${regionId}`),
      [t],
    )

    const hasAnyRegionExpanded = expandedRegions.size > 0

    const regionsToShow = useMemo(
      () => REGIONS.filter((r) => expandedRegions.has(r.id)),
      [expandedRegions],
    )

    async function handleContinue() {
      setIsSaving(true)
      try {
        await usersApi.updateTargetingPreferences({
          targetingPreferences: { focusMarkets: selectedCountries },
        })
        track(AnalyticsEvents.ONBOARDING_INDUSTRY_SELECTED, {
          markets_count: selectedCountries.length,
        })
        onNext()
      } catch {
        toast.warning(t('markets.saveWarning'))
        onNext()
      } finally {
        setIsSaving(false)
      }
    }

    return (
      <div ref={ref} tabIndex={-1} className={cn('flex flex-col gap-6 outline-none', className)}>
        {/* Heading */}
        <FadeUp delay={0} className="text-center">
          <Heading level={1} size="4xl">
            {t('markets.title')}
          </Heading>
          <Text size="sm" color="muted" className="mt-2">
            {t('markets.subtitle')}
          </Text>
        </FadeUp>

        {/* Region pills */}
        <FadeUp delay={120}>
          <div
            role="group"
            aria-label={t('markets.regionsAria')}
            className="flex flex-wrap justify-center gap-2"
            data-testid="markets-region-pills"
          >
            {REGIONS.map((region) => (
              <ToggleChip
                key={region.id}
                variant="platform"
                tone={tone}
                selected={expandedRegions.has(region.id)}
                data-testid={`markets-region-${region.id}`}
                onClick={() => toggleRegion(region.id)}
              >
                {regionLabel(region.id)}
              </ToggleChip>
            ))}
          </div>
        </FadeUp>

        {/* Expanded country grids */}
        <div className="flex flex-col gap-4" data-testid="markets-country-sections">
          <AnimatePresence initial={false}>
            {regionsToShow.map((region) => (
              <motion.div
                key={region.id}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => toggleAllInRegion(region.countries)}
                    className={cn(
                      'flex items-center justify-center gap-1.5 w-full text-sm font-medium transition-colors',
                      region.countries.every((c) => selectedCountries.includes(c))
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {regionLabel(region.id)}
                    <span className="text-sm opacity-60">
                      {region.countries.every((c) => selectedCountries.includes(c)) ? t('markets.deselectAll') : t('markets.selectAll')}
                    </span>
                  </button>
                  <div
                    role="group"
                    aria-label={t('markets.countriesAria', { region: regionLabel(region.id) })}
                    className="flex flex-wrap justify-center gap-2"
                    data-testid={`markets-countries-${region.id}`}
                  >
                    {region.countries.map((code) => (
                      <ToggleChip
                        key={`${region.id}-${code}`}
                        variant="platform"
                        tone={tone}
                        size="sm"
                        selected={selectedCountries.includes(code)}
                        showCheck
                        data-testid={`markets-country-${code.toLowerCase()}`}
                        onClick={() => toggleCountry(code)}
                      >
                        {countryName(code, uiLocale)}
                      </ToggleChip>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Continue + skip */}
        <FadeUp delay={hasAnyRegionExpanded ? 0 : 200} className="flex flex-col gap-2">
          <Button
            data-testid="markets-continue-button"
            variant="cta"
            onClick={handleContinue}
            disabled={isSaving || selectedCountries.length === 0}
            className="w-full"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('saving', { ns: 'common' })}
              </>
            ) : (
              t('continue', { ns: 'common' })
            )}
          </Button>
          <button
            type="button"
            data-testid="markets-skip-button"
            onClick={onSkip}
            className="text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('changeLater', { ns: 'common' })}
          </button>
        </FadeUp>
      </div>
    )
  },
)
